import React from "react";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import {
  PlannerBlock, MelbourneSection, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { blogsForService } from "../data/internalLinks";

export default function GoogleBusinessProfilePhotos() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <WhyMatters />
      <CategoryGrid />
      <CoverageMix />
      <PlannerBlock
        eyebrow="Plan it now · Interactive estimator"
        headline="Cost a complete Google Profile"
        italic="refresh in under a minute."
        blurb="Slide for menu size, tick venue + exterior, see the price. Most Google Business refreshes come in between $450 and $900 — short shoot, big visibility lift."
      />
      <MelbourneSection
        serviceLabel="Google Business Photos"
        eyebrow="Local SEO · Melbourne mobile coverage"
        headline="Google Business photos for"
        italic="every Melbourne suburb."
        intro="Google Business Profile photography for venues across Greater Melbourne — CBD, Brunswick, Carlton, Richmond, Fitzroy, Footscray, Glen Waverley, Dandenong, Point Cook and the inner west. Files uploaded the same week."
      />
      <SisterServices exclude="gbp" keys={["menu", "venue", "cafe", "ubereats"]} />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on Google"
        italic="and local search."
        theme="ink"
        cols={3}
        items={blogsForService("google-business-profile-photos")}
      />
      <FAQSection
        eyebrow="Common questions · Google Business photos Melbourne"
        headline="Visibility, coverage,"
        italic="and uploads."
        intro="A Google Business Profile refresh is one of the highest-leverage shoots a small venue can run. Most quotes confirmed in under five minutes on the phone."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to refresh your"
        italic="Google profile?"
        intro="Quick form below or call directly. We'll often glance at your current profile first to scope exactly what's missing."
        backdropSlug="venue-buffet-01"
        formTitle="Tell us about your venue."
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="venue-buffet-01" alt="Google Business Profile photos Melbourne — restaurant interior" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Google Business Profile Photos Melbourne" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Google Business Photography Melbourne · Maps · Local SEO
        </p>

        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}>
          The first impression
          <br />
          is on <span className="italic font-light text-ember">Google.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Google Business Profile photography for restaurants, cafés, bars and
          hospitality venues. Cover, interior, exterior, menu items and team
          imagery — sized and uploaded for Google Maps, Search and your
          listing.
        </p>

        <p className="mt-7 max-w-xl">
          <span className="inline-block bg-ember text-bone font-bold text-sm md:text-base uppercase tracking-wider2 px-4 py-2.5">
            Profile Refresh From $450
          </span>
        </p>
        <p className="mt-3 text-xs text-bone/55 max-w-md leading-relaxed">
          Short on-location session covering cover + interior + exterior + 10
          hero menu items. Full refreshes from $750 when combined with a
          venue or menu day.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#enquiry" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Get A Profile Quote</CTAButton>
          </a>
          <a href="#planner" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Plan The Shoot</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Session", "2–4 hours"],
          ["Turnaround", "3–5 days"],
          ["Uploads", "Google-ready"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider2 text-ember mb-1.5 font-bold">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyMatters = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Why Google Business photos matter
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Google is the new
            <br />
            <span className="italic font-light text-ember">storefront.</span>
          </h2>

          <div className="mt-12 grid gap-7 text-bone/85 leading-relaxed text-base md:text-lg">
            <ScrollReveal>
              <p>
                Most people decide whether to visit your restaurant on Google
                — the listing card, the photos, the star rating, the cover
                image. A profile with bright, professional photos
                <strong className="text-bone"> earns more clicks, more direction requests, and more bookings</strong> than one with phone snaps.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <p>
                Google ranks profiles with regular, high-quality photo uploads
                higher in Maps and local pack results. A single professional
                refresh — cover, interior, exterior, dishes — typically lifts
                your profile to the top tier of Google's image diversity
                signal.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p>
                We shoot to Google's spec: 4:3 cover, 16:9 supporting,
                portrait for dishes, exterior with signage in frame. Files
                arrive sized and named — ready to drop into your profile in
                ten minutes.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={280}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                {[
                  "Profile cover photo",
                  "Interior & ambience",
                  "Exterior & signage",
                  "10 hero menu items",
                  "Team / behind-the-scenes",
                  "Google-spec file sizing",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check size={16} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-3 md:space-y-5 lg:mt-4">
          <Photo slug="hero-banquet-01" alt="Google Business interior photo Melbourne" ratio="4/5" sizes="(min-width: 1024px) 35vw, 100vw" />
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <Photo slug="menu-butter-chicken" alt="Google Business menu photo Melbourne" ratio="1/1" sizes="20vw" />
            <Photo slug="hero-spread-01" alt="Google Business cover photo Melbourne" ratio="1/1" sizes="20vw" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const COVERS = [
  { n: "01", title: "Cover photo", slug: "hero-banquet-01", desc: "The single image that loads first on your Google profile — 4:3 ratio, branded, scene-setting. Usually the most important photo on the listing." },
  { n: "02", title: "Profile logo / identity", slug: "menu-malai-tikka", desc: "Square logo and brand identity image — what shows next to your reviews. Usually a tight hero dish or signage shot." },
  { n: "03", title: "Interior & ambience", slug: "hero-banquet-01", desc: "Dining room, lighting, tables set, the mood guests walk into. Critical for restaurant and bar profiles." },
  { n: "04", title: "Exterior & signage", slug: "hero-spread-01", desc: "Street-front, signage, A-frame, laneway view. The image that tells customers how to find you in real life." },
  { n: "05", title: "Menu items (top 10)", slug: "menu-butter-chicken", desc: "Ten hero dishes shot in portrait orientation — the photos that show up in the 'Menu' tab of your listing." },
  { n: "06", title: "Team & service", slug: "venue-buffet-01", desc: "Chef at pass, barista pulling shots, owner on the floor. Adds humanity and lifts engagement on the profile." },
  { n: "07", title: "By appointment", slug: "hero-laddu-220", desc: "Function rooms, private dining, special events — supporting imagery for the deeper tabs of your listing." },
  { n: "08", title: "Seasonal refreshes", slug: "menu-veg-crispy", desc: "Quarterly photo drops to keep the profile ranking — Google rewards venues that upload regularly." },
];

const CategoryGrid = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What we shoot · Google Business library
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Every tab of your
            <br />
            <span className="italic font-light text-ember">Google profile.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            Cover, identity, interior, exterior, menu, team. One session
            covers every photo type Google's algorithm wants to see — and the
            quarterly refresh to keep it ranking.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {COVERS.map((c) => (
          <article key={c.n} className="bg-ink hover:bg-[#161613] transition-colors p-5 md:p-6 flex flex-col">
            <Photo slug={c.slug} alt={`${c.title} — Google Business photo Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 50vw" />
            <div className="mt-5 flex flex-col flex-1">
              <span className="font-display italic text-2xl text-ember leading-none">{c.n}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-2.5">{c.title}</h3>
              <p className="mt-3 text-sm text-bone/70 leading-relaxed flex-1">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const CoverageMix = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-6">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            The right mix · Profile sweep
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            What a complete
            <br />
            <span className="italic font-light text-ember">refresh looks like.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed">
            A profile refresh is more than just dish photos. We shoot a
            balanced library across every Google category so your listing
            looks complete — and ranks for image diversity in Maps.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
        <Photo slug="hero-banquet-01" alt="Google Business cover Melbourne" ratio="4/5" sizes="20vw" />
        <Photo slug="hero-banquet-01" alt="Google Business interior Melbourne" ratio="4/5" sizes="20vw" />
        <Photo slug="menu-butter-chicken" alt="Google Business menu item Melbourne" ratio="4/5" sizes="20vw" />
        <Photo slug="venue-buffet-01" alt="Google Business service Melbourne" ratio="4/5" sizes="20vw" />
      </div>
    </div>
  </section>
);

const FAQS = [
  { q: "How does Google Business Profile photography help my restaurant rank higher?", a: "Google's local algorithm rewards profiles with a complete photo library (cover, interior, exterior, food, team), regular uploads, and high-quality imagery. Venues that refresh their Google Business photos typically see lifts in profile views, direction requests and 'website click' actions within 2–4 weeks." },
  { q: "Do you upload the photos to my Google profile?", a: "We can — for a small additional service fee — or we deliver them named and sized so your in-house team can drop them in via Google Business Profile Manager in about 10 minutes." },
  { q: "What's included in a Profile Refresh?", a: "A short on-location shoot (2–4 hours) covering: 1 cover image, 1 identity / logo-friendly shot, 3–5 interior frames, 2 exterior frames, 10 hero menu items shot vertical for the Menu tab, and 2–3 team or service shots. Delivered in a Google-spec folder." },
  { q: "How often should I refresh Google Business photos?", a: "Quarterly is ideal — Google rewards venues that upload regularly. Many of our café and restaurant clients book a half-day shoot every 3 months that covers seasonal menu changes and adds fresh ambience imagery." },
  { q: "Can you also shoot for Google Maps Street View?", a: "Standard street-view contributions are uploaded through Google's contributor flow — we shoot the imagery and you (or your marketing team) upload it from your Google account so the listing properly attributes to your business." },
  { q: "Which Melbourne suburbs do you cover?", a: "All of Greater Melbourne — CBD, Carlton, Fitzroy, Brunswick, Richmond, South Yarra, St Kilda, Prahran, Footscray, Point Cook, Tarneit, Werribee, Sunshine, Epping, Craigieburn, Preston, Dandenong, Glen Waverley, Clayton and Springvale." },
];
